import { useEffect, useState } from "react";
import { Loader2, Wand2, Undo2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString([], {
  month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
}) : "");

/**
 * Iter 120 — Bulk table tidy. Snaps every imported table to one saved preset
 * in a single pass; each run is undoable because the server snapshots the
 * previous body of every article it touches.
 */
export default function KbTidyTables() {
  const [presets, setPresets] = useState([]);
  const [presetId, setPresetId] = useState("");
  const [runs, setRuns] = useState([]);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);

  const loadRuns = () => api.get("/kb/tables/tidy/runs")
    .then(({ data }) => setRuns(data.runs || [])).catch(() => {});

  useEffect(() => {
    api.get("/kb/table-presets").then(({ data }) => {
      setPresets(data.presets || []);
      const house = (data.presets || []).find((p) => /house/i.test(p.name));
      setPresetId(house?.preset_id || data.presets?.[0]?.preset_id || "");
    }).catch(() => {});
    loadRuns();
  }, []);

  const run = async () => {
    setConfirm(false);
    setBusy(true);
    try {
      const { data } = await api.post("/kb/tables/tidy", { preset_id: presetId });
      toast.success(`Tidied ${data.tables} table${data.tables === 1 ? "" : "s"} across ${data.articles} article${data.articles === 1 ? "" : "s"}`);
      loadRuns();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Tidy failed");
    } finally { setBusy(false); }
  };

  const undo = async (runId) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/kb/tables/tidy/${runId}/undo`);
      toast.success(`Restored ${data.restored} article${data.restored === 1 ? "" : "s"}`);
      loadRuns();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Undo failed");
    } finally { setBusy(false); }
  };

  const latest = runs[0];

  return (
    <section className="mt-6 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5" data-testid="kb-tidy-tables">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Wand2 className="h-4 w-4 text-primary" />
          <h2 className="font-heading text-base font-semibold text-slate-800 dark:text-slate-100">Tidy imported tables</h2>
        </div>
        <div className="flex items-center gap-2">
          <select value={presetId} onChange={(e) => setPresetId(e.target.value)}
            data-testid="kb-tidy-preset" className="h-9 rounded-md border border-slate-200 dark:border-slate-800 px-2 text-sm">
            {presets.length === 0 && <option value="">No presets saved</option>}
            {presets.map((p) => <option key={p.preset_id} value={p.preset_id}>{p.name}</option>)}
          </select>
          <Button size="sm" disabled={busy || !presetId} data-testid="kb-tidy-run"
            onClick={() => setConfirm(true)}
            className="bg-primary text-primary-foreground hover:bg-primary/90">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} Tidy all tables
          </Button>
        </div>
      </div>
      <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
        Rewrites the borders, shading, padding and alignment of every table in the Knowledge Base
        to the selected preset — handy for the tables carried over from Helpjuice. Each run is
        undoable.
      </p>

      {latest && (
        <div className="mt-4 flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/60 px-3 py-2 text-sm"
          data-testid="kb-tidy-last-run">
          <span className="text-slate-600 dark:text-slate-300">
            Last run {fmt(latest.created_at)} — <span className="font-medium">{latest.tables}</span> tables in{" "}
            <span className="font-medium">{latest.articles}</span> articles → {latest.preset_name}
          </span>
          {latest.undone_at
            ? <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[11px] font-medium text-slate-600 dark:text-slate-300">undone {fmt(latest.undone_at)}</span>
            : (
              <Button variant="outline" size="sm" disabled={busy} data-testid="kb-tidy-undo"
                onClick={() => undo(latest.run_id)} className="ml-auto">
                <Undo2 className="h-4 w-4" /> Undo this run
              </Button>
            )}
        </div>
      )}

      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid="kb-tidy-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Restyle every table in the Knowledge Base?</AlertDialogTitle>
            <AlertDialogDescription>
              This updates the borders, shading, padding and alignment of every table in every
              article to the &ldquo;{presets.find((p) => p.preset_id === presetId)?.name}&rdquo; preset.
              We snapshot each article first, so you can undo the whole run from this page.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="kb-tidy-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={run} data-testid="kb-tidy-confirm-run">Tidy all tables</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
